import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import SignatureCanvas from "react-signature-canvas";

import { BASE_URL, ORDER_CONFIRMATION_URL, POD_URL } from "../../Routes/urls";
import { AppDispatch, RootState } from "../../store";
import { orderActions } from "../../store/slices/OrderSlice";
import { addOrder } from "../../services/orders";
import {
  addLocalOffsetToTimestamp,
  getClasses,
  getLocalDateString,
  getStartOfDayTimestamp,
  getStates,
  validateEmail,
} from "../../utils";
import { InfoQuestionType, InsuranceStatus } from "../../ts/enums";
import { SelectedService } from "../../ts/models/Event";
import classes from "./ContactDetails.module.css";
import OrderCard from "./OrderCard";
import InputLabel from "../../components/Inputs/InputLabel";
import SelectButton from "../../components/SelectButton/SelectButton";
import Patient, { PatientDetails } from "../../ts/models/Patient";
import Order, { InfoQuestionResponse } from "../../ts/models/Order";
import Checkbox from "../../components/Checkbox/Checkbox";
import Card from "../../components/Card/Card";
import Input from "../../components/Inputs/Input";
import PrimaryButton from "../../components/Buttons/PrimaryButton";
import Select from "../../components/Inputs/Select";
import ServicesWarning from "./ServicesWarning";
import CommonMaster from "../../ts/models/CommonMaster";
import InfoQuestion from "../../ts/models/InfoQuestion";
import MultiSelect, {
  MultiSelectOption,
} from "../../components/Inputs/MultiSelect";
import Header from "../Header/Header";
import LinkButton from "../../components/Buttons/LinkButton";
import RadioInput from "../../components/Inputs/RadioInput";

const getPatient = () => {
  // Try to get the patient data from local storage first.
  const patientData = localStorage.getItem("patientData");
  if (patientData) {
    return JSON.parse(patientData);
  }

  let patient = new Patient();
  patient.country = "USA";
  patient.state = "IL"; // Set Illinois as the default state
  patient.createAccount = true;
  return patient;
};

const isPatientPortalData = () => {
  return !!localStorage.getItem("isPatientPortalData");
};

const getMasterId = (data: string, masterData: CommonMaster[]) => {
  return (
    masterData.find(
      (master) =>
        master.description?.toLowerCase() === data?.toLowerCase() ||
        master.id === data
    )?.id || ""
  );
};

const getRelationToPatientId = (
  relationshipToPatient: string,
  relationships: CommonMaster[]
) => {
  if (relationshipToPatient) {
    return (
      relationships.find(
        (data) =>
          data.description.toLocaleLowerCase() ===
          relationshipToPatient.toLocaleLowerCase()
      )?.id || ""
    );
  } else {
    return "";
  }
};

const getInitialFieldErrors = () => {
  let infoQuestionsErrors: boolean[] = [];
  return {
    regEmail: false,
    regPhoneNo: false,
    firstName: false,
    lastName: false,
    dob: false,
    gender: false,
    race: false,
    ethnicity: false,
    address: false,
    zipCode: false,
    city: false,
    country: false,
    state: false,
    providerName: false,
    policyNumber: false,
    relationshipToPatient: false,
    infoQuestions: infoQuestionsErrors,
    primaryMemberDob: false,
    primaryMemberName: false,
    providerName2: false,
    policyNumber2: false,
    signature: false,
  };
};

const options = {
  signatureOnFile: "SIGNATURE_ON_FILE",
  signHere: "SIGN_HERE",
};

type Props = {};

const ContactDetails: React.FC<Props> = () => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const {
    pod,
    disclaimers,
    infoQuestions: podInfoQuestions,
    resourceName,
    productInfoQuestions,
  } = useSelector((state: RootState) => state.pod);
  const { genders, ethnicGroups, relationships, races } = useSelector(
    (state: RootState) => state.masters
  );
  const { currEvent, selectedDate } = useSelector(
    (state: RootState) => state.events
  );
  const { selectedServiceProducts } = useSelector(
    (state: RootState) => state.order
  );

  const activeGenders = genders.filter((gender) => {
    return gender.isActive;
  });
  const activeEthnicGroups = ethnicGroups.filter((ethnicGroup) => {
    return ethnicGroup.isActive;
  });
  const activeRelationships = relationships.filter((relationship) => {
    return relationship.isActive;
  });
  const activeRaces = races.filter((race) => {
    return race.isActive;
  });
  const savedPatientData: PatientDetails = getPatient();

  const adjustPatientData = () => {
    const gender = getMasterId(savedPatientData?.gender, activeGenders);
    const race = getMasterId(savedPatientData?.race, activeRaces);
    const ethnicity = getMasterId(
      savedPatientData?.ethnicity,
      activeEthnicGroups
    );
    const state =
      getStates.find(
        (data) =>
          data.stateName.toLocaleLowerCase() ===
            savedPatientData.state.toLocaleLowerCase() ||
          data.alphaCode.toLocaleLowerCase() ===
            savedPatientData.state.toLocaleLowerCase()
      )?.alphaCode || "IL";
    const dob = savedPatientData.dob
      ? addLocalOffsetToTimestamp(Number(savedPatientData.dob))
      : "";
    const primaryMemberDob = savedPatientData.primaryMemberDob
      ? addLocalOffsetToTimestamp(Number(savedPatientData.primaryMemberDob))
      : "";

    return {
      ...savedPatientData,
      gender: gender,
      race: race,
      ethnicity: ethnicity,
      state: state,
      dob: dob,
      primaryMemberDob: primaryMemberDob,
    };
  };

  const [patient, setPatient] = useState<PatientDetails>(adjustPatientData());
  const [orderData, setOrderData] = useState<Order>({
    ...new Order(),
    regEmail: savedPatientData?.email || "",
    regPhoneNo: savedPatientData?.phoneNo || "",
    relationshipToPatient:
      isPatientPortalData() && !!savedPatientData.relationshipToPatient
        ? getRelationToPatientId(
            savedPatientData.relationshipToPatient,
            relationships
          )
        : "",
  });
  const [isDisclaimerChecked, setIsDisclaimerChecked] = useState<boolean[]>([]);
  const [fieldErrors, setFieldErrors] = useState(getInitialFieldErrors());
  const [insuranceOpt, setInsuranceOpt] = useState(
    savedPatientData.haveInsurance ? InsuranceStatus.Yes : InsuranceStatus.No
  );
  const [primaryOpt, setPrimaryOpt] = useState(
    savedPatientData.isPrimaryMember ? InsuranceStatus.Yes : InsuranceStatus.No
  );
  const [secondaryInsuranceOpt, setSecondaryInsuranceOpt] = useState(
    savedPatientData.haveSecondaryInsurance
      ? InsuranceStatus.Yes
      : InsuranceStatus.No
  );
  const [infoQuestionResponses, setInfoQuestionResponses] = useState<
    InfoQuestionResponse[]
  >([]);
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState("");
  const [unavailableServices, setUnavailableServices] = useState<
    SelectedService[]
  >([]);
  const [orderServices, setOrderServices] = useState<SelectedService[]>(
    selectedServiceProducts
  );
  const [signatureDisclaimer, setSignatureDisclaimer] = useState(false);
  let padRef = useRef<SignatureCanvas>(null);
  const [selected, setSelected] = useState("SIGNATURE_ON_FILE");

  const allDisclaimersAccepted = isDisclaimerChecked.every(
    (checked) => checked === true
  );

  const infoQuestions: InfoQuestion[] = Array.from(
    [...podInfoQuestions, ...productInfoQuestions]
      .reduce((map, question) => {
        map.set(question.id, question);
        return map;
      }, new Map())
      .values()
  );

  useEffect(() => {
    setPatient((prevState) => ({ ...prevState, eventIds: [currEvent.id] }));
  }, [currEvent]);

  useEffect(() => {
    const checked = new Array(disclaimers.length).fill(false);
    setIsDisclaimerChecked(checked);
  }, [disclaimers]);

  const isDOBNotInRange = (dob: string | number) => {
    if (typeof dob === "number") {
      if (
        dob < new Date("1900-01-01").getTime() ||
        dob > new Date().getTime()
      ) {
        return true;
      }
    }
    // "", NaN or out of range
    return false;
  };

  const getDOBInvalidText = (dob: string | number) => {
    if (dob === "") {
      return "This field is required";
    }

    if (isDOBNotInRange(dob)) {
      return "Must be 01/01/1900 or later and no later than today";
    }

    return "";
  };

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const isFieldsValid = (): boolean => {
    const fieldErrors = getInitialFieldErrors();
    let sectionId = "";

    if (
      (!currEvent?.regEmailOptional &&
        (!orderData.regEmail?.trim() || !validateEmail(orderData.regEmail))) ||
      (currEvent?.regEmailOptional === true &&
        !!orderData.regEmail?.trim() &&
        !validateEmail(orderData.regEmail))
    ) {
      fieldErrors.regEmail = true;
      sectionId = "section1";
    }
    if (!orderData.regPhoneNo || orderData.regPhoneNo.length !== 10) {
      fieldErrors.regPhoneNo = true;
      sectionId = "section1";
    }
    if (!patient.firstName?.trim()) {
      fieldErrors.firstName = true;
      sectionId = "section1";
    }
    if (!patient.lastName?.trim()) {
      fieldErrors.lastName = true;
      sectionId = "section1";
    }
    if (!patient.dob) {
      fieldErrors.dob = true;
      sectionId = "section1";
    } else if (isDOBNotInRange(patient.dob)) {
      fieldErrors.dob = true;
      sectionId = "section1";
    }
    if (!patient.gender) {
      fieldErrors.gender = true;
      sectionId = "section2";
    }
    if (!patient.race) {
      fieldErrors.race = true;
      sectionId = "section2";
    }
    if (!patient.ethnicity) {
      fieldErrors.ethnicity = true;
      sectionId = "section2";
    }
    if (!patient.address) {
      fieldErrors.address = true;
      sectionId = "section2";
    }
    if (!patient.zipCode) {
      fieldErrors.zipCode = true;
      sectionId = "section2";
    }
    if (!patient.city) {
      fieldErrors.city = true;
      sectionId = "section2";
    }
    if (!patient.country) {
      fieldErrors.country = true;
      sectionId = "section2";
    }
    if (!patient.state) {
      fieldErrors.state = true;
      sectionId = "section2";
    }
    if (patient.haveInsurance) {
      if (!patient.insuranceDetails.providerName) {
        fieldErrors.providerName = true;
        sectionId = "section3";
      }
      if (!patient.insuranceDetails.policyNumber) {
        fieldErrors.policyNumber = true;
        sectionId = "section3";
      }
      if (patient.haveSecondaryInsurance) {
        if (!patient.secondaryInsuranceDetails.providerName) {
          fieldErrors.providerName2 = true;
          sectionId = "section3";
        }
        if (!patient.secondaryInsuranceDetails.policyNumber) {
          fieldErrors.policyNumber2 = true;
          sectionId = "section3";
        }
      }
      if (!patient.isPrimaryMember) {
        if (!patient.primaryMemberName) {
          fieldErrors.primaryMemberName = true;
          sectionId = "section3";
        }

        if (!patient.primaryMemberDob) {
          fieldErrors.primaryMemberDob = true;
          sectionId = "section3";
        } else if (isDOBNotInRange(patient.primaryMemberDob)) {
          fieldErrors.primaryMemberDob = true;
          sectionId = "section3";
        }
      }
    }
    if (!orderData.relationshipToPatient) {
      fieldErrors.relationshipToPatient = true;
      sectionId = "section4";
    }

    if (
      (!currEvent.regAllowSignatureOnFile || options.signHere === selected) &&
      padRef.current?.isEmpty()
    ) {
      fieldErrors.signature = true;
      sectionId = "section4";
    }

    infoQuestions.forEach((infoQuestion, idx) => {
      const questionResponse = getQuestionResponse(infoQuestion.logicQuestion);
      if (
        infoQuestion.logicQuestion &&
        infoQuestion.logicQuestionResponse &&
        questionResponse &&
        infoQuestion.logicQuestionResponse
          .split(",")
          .some((word) => questionResponse.split(",").includes(word)) &&
        (!infoQuestionResponses[idx] ||
          !infoQuestionResponses[idx]?.responseText)
      ) {
        fieldErrors.infoQuestions[idx] = true;
        sectionId = "section3";
      } else if (
        !infoQuestion.logicQuestion &&
        (!infoQuestionResponses[idx] ||
          !infoQuestionResponses[idx]?.responseText)
      ) {
        fieldErrors.infoQuestions[idx] = true;
        sectionId = "section3";
      } else {
        fieldErrors.infoQuestions[idx] = false;
      }
    });

    setFieldErrors(fieldErrors);

    if (sectionId) {
      // Bring the error field into view
      scrollToSection(sectionId);
    }

    return Object.values(fieldErrors).every((value) => {
      if (typeof value === "boolean") {
        return value === false;
      } else {
        return value.every((subValue) => subValue === false);
      }
    });
  };

  const clearFieldErrors = (key: keyof typeof fieldErrors, idx?: number) => {
    if (key === "infoQuestions") {
      if (idx) {
        setFieldErrors((prevState) => {
          const newState = { ...prevState };
          newState[key][idx] = false;
          return newState;
        });
      }
    } else {
      if (fieldErrors[key])
        setFieldErrors((prevState) => {
          const newState = { ...prevState };
          newState[key] = false;
          return newState;
        });
    }
  };

  const infoQuestionResponseHandler = (index: number, responseText: string) => {
    setInfoQuestionResponses((prevState) => {
      const newState: any = [...prevState];
      newState[index] = {
        questionId: infoQuestions[index].id,
        responseText,
      };

      if (
        infoQuestions.some((q) => {
          return (
            q.logicQuestion === infoQuestions[index].id &&
            !q.logicQuestionResponse
              .split("|")
              .some((word) => responseText.split("|").includes(word))
          );
        })
      ) {
        clearDependentAnswers(newState, infoQuestions[index].id);
      }
      return newState;
    });
    clearFieldErrors("infoQuestions", index);
  };

  const clearDependentAnswers = (
    response: InfoQuestionResponse[],
    questionId: string
  ) => {
    const dependentQuestions = infoQuestions.filter(
      (q) => q.logicQuestion === questionId
    );
    let updatedResponse = [...response];
    dependentQuestions.forEach((question) => {
      if (question?.id) {
        let index = -1;
        index = updatedResponse.findIndex((e) => e?.questionId === question.id);
        if (index !== -1) {
          delete updatedResponse[index];
          if (question.logicQuestion) {
            updatedResponse = clearDependentAnswers(
              updatedResponse,
              question.id
            );
          }
        }
      }
    });

    setTimeout(() => {
      setInfoQuestionResponses(updatedResponse);
    }, 0);
    return updatedResponse;
  };

  const getQuestionResponse = (questionId: string) => {
    if (infoQuestionResponses.length) {
      const questionResponse =
        infoQuestionResponses?.find(
          (question) => question?.questionId === questionId
        )?.responseText || "";
      return questionResponse;
    }
  };

  const isQuestionVisible = (infoQuestion: InfoQuestion) => {
    const questionResponse =
      getQuestionResponse(infoQuestion.logicQuestion) || "null";
    const logicResponse = infoQuestion.logicQuestionResponse;

    if (infoQuestion.logicQuestion) {
      // Case where both questionResponse and logicQuestionResponse have commas
      if (questionResponse.includes(",") && logicResponse.includes(",")) {
        const questionParts = questionResponse.split(",");
        const responseParts = logicResponse.split(",");
        return responseParts.some((data) => questionParts.includes(data));
      }
      // Case where only questionResponse has commas
      if (questionResponse.includes(",")) {
        return questionResponse.includes(logicResponse);
      }
      // Case where only logicQuestionResponse has commas
      if (logicResponse.includes(",")) {
        return logicResponse.includes(questionResponse);
      }
      return questionResponse === logicResponse;
    }

    return true;
  };

  const clearSignatureHandler = () => {
    padRef.current?.clear();
  };

  const addOrderHandler = async () => {
    if (!isFieldsValid()) {
      return;
    }

    const infoQuesResponse = infoQuestionResponses.filter(
      (e) =>
        e !== undefined && e.responseText !== undefined && e.responseText !== ""
    );

    const regEmail = orderData.regEmail?.trim() || "";

    const order = {
      ...orderData,
      regEmail,
      podId: pod.id,
      eventId: currEvent.id,
      services: orderServices,
      infoQuestionResponses: infoQuesResponse,
      appointmentTime:
        selectedDate - new Date(selectedDate).getTimezoneOffset() * 60000,
    };

    delete patient.email;
    delete patient.phoneNo;
    delete patient.relationshipToPatient;
    if (isPatientPortalData()) {
      delete patient.createAccount;
    }

    // Trim name of the patient.
    const firstName = patient.firstName?.trim();
    const middleName = patient.middleName?.trim() || "";
    const lastName = patient.lastName?.trim();

    // Adjust patient's date of birth to UTC.
    let dob = patient.dob;
    if (typeof dob === "number") {
      dob = dob - new Date(dob).getTimezoneOffset() * 60000; // Get timezone offset on the same date to take care of DST
    }

    // Adjust primary member's date of birth to UTC.
    let primaryMemberDob = patient.primaryMemberDob;
    if (typeof primaryMemberDob === "number") {
      primaryMemberDob =
        primaryMemberDob -
        new Date(primaryMemberDob).getTimezoneOffset() * 60000; // Get timezone offset on the same date to take care of DST
    }

    const newPatient: Patient = {
      ...patient,
      firstName,
      middleName,
      lastName,
      dob,
      primaryMemberDob,
    };

    const formData = new FormData();
    formData.append(
      "orderPatient",
      JSON.stringify({ order, patient: newPatient })
    );
    if (!padRef.current?.isEmpty()) {
      formData.append("signatureImage", padRef.current?.toDataURL() || "");
    }

    const { status, data } = await addOrder(formData);

    if (status === 200) {
      if (data.success === false) {
        localStorage.setItem("patientData", JSON.stringify(newPatient));
        if (data.errorCode === "event_services_fully_booked") {
          // Check if none of the services asked for is available.
          if (orderServices.length === data.services.length) {
            setMessage(
              "Sorry! The services you selected are fully booked on this date."
            );

            setOrderServices([]);
            setUnavailableServices([]);
          } else {
            // Remove the unavailable services from the ordered services.
            setOrderServices((prevState) => {
              let newState = [...prevState];
              return newState.filter((orderService) => {
                const matchingService = data.services.find(
                  (service: any) => service.serviceId === orderService.serviceId
                );
                return !matchingService;
              });
            });

            setMessage(
              "Sorry! The services below are fully booked on this date."
            );

            setUnavailableServices(data.services);
          }

          setShowModal(true);
        }
      } else {
        dispatch(orderActions.setOrder(data));
        dispatch(orderActions.setServicesWithProducts(orderServices));
        navigate(
          `${BASE_URL}/${resourceName}/${POD_URL}/${pod.id}/event/${currEvent.id}/${ORDER_CONFIRMATION_URL}/${data.id}${window.location.search}`
        );
        localStorage.removeItem("patientData");
        localStorage.removeItem("isPatientPortalData");
      }
    }
  };

  const insuranceOptions = [InsuranceStatus.Yes, InsuranceStatus.No];

  const closeHandler = (proceedWithOrder: boolean) => {
    setShowModal(false);
    if (proceedWithOrder) {
      addOrderHandler();
    }
  };

  return (
    <>
      {showModal && (
        <ServicesWarning
          message={message}
          unavailableServices={unavailableServices}
          onClose={closeHandler}
        />
      )}
      <Header />
      <div className={classes.main}>
        <div className={classes.top}>
          <Card>
            <div id='section1' className={classes.heading}>
              Share your Contact Details
            </div>
            <Input
              inputClassName={classes.input}
              placeholder={"Enter Your Email Id"}
              value={orderData.regEmail}
              onChange={(e) => {
                setOrderData((prevState) => {
                  return { ...prevState, regEmail: e.target.value };
                });
                clearFieldErrors("regEmail");
              }}
              isInvalid={fieldErrors.regEmail}
              invalidText='Invalid email'
              name={"Email Id"}
              disabled={isPatientPortalData()}
              optional={currEvent?.regEmailOptional || false}
            />
            <div className={classes.selectContainer}>
              <Select
                className={classes.country}
                disabled
                label={""}
                htmlFor='country'
                options={[{ name: "+ USA", id: "1" }]}
                value={"1"}
                nameExtractor='name'
                onChange={() => {}}
              />
              <Input
                inputClassName={classes.input}
                style={{ flex: 5 }}
                type='number'
                value={orderData.regPhoneNo}
                onChange={(e) => {
                  setOrderData((prevState) => {
                    return { ...prevState, regPhoneNo: e.target.value };
                  });
                  clearFieldErrors("regPhoneNo");
                }}
                isInvalid={fieldErrors.regPhoneNo}
                invalidText='Must be 10 digits'
                placeholder={""}
                name={"Phone Number"}
                disabled={isPatientPortalData()}
              />
            </div>
          </Card>
          <Card className={classes.patientCard}>
            <div className={classes.heading}>Patient's Information</div>
            <div className={classes.boxContainer}>
              <Input
                inputClassName={classes.input}
                placeholder={"First Name"}
                value={patient.firstName}
                onChange={(e) => {
                  setPatient((prevState) => {
                    return { ...prevState, firstName: e.target.value };
                  });
                  clearFieldErrors("firstName");
                }}
                isInvalid={fieldErrors.firstName}
                invalidText='This field is required'
                name={"First Name"}
              />
              <Input
                inputClassName={classes.input}
                placeholder={"Middle Name"}
                value={patient.middleName}
                onChange={(e) => {
                  setPatient((prevState) => {
                    return { ...prevState, middleName: e.target.value };
                  });
                }}
                name={"Middle Name"}
              />
              <Input
                inputClassName={classes.input}
                placeholder={"Last Name"}
                value={patient.lastName}
                onChange={(e) => {
                  setPatient((prevState) => {
                    return { ...prevState, lastName: e.target.value };
                  });
                  clearFieldErrors("lastName");
                }}
                isInvalid={fieldErrors.lastName}
                invalidText='This field is required'
                name={"Last Name"}
              />
              <Input
                type='date'
                placeholder=''
                min='1900-01-01'
                max={getLocalDateString(new Date().getTime())}
                value={
                  typeof patient.dob === "number"
                    ? getLocalDateString(patient.dob)
                    : ""
                }
                onChange={(e) => {
                  setPatient((prevState) => {
                    return {
                      ...prevState,
                      dob: getStartOfDayTimestamp(e.target.value),
                    };
                  });
                  clearFieldErrors("dob");
                }}
                isInvalid={fieldErrors.dob}
                invalidText={getDOBInvalidText(patient.dob)}
                name={"Patient's Date Of Birth"}
              />
            </div>
            <div id='section2' className={classes.gender}>
              <Select
                className={classes.dropDown}
                selectClass={classes.dropDownSelect}
                label={"Gender"}
                htmlFor='gender'
                options={activeGenders}
                value={patient?.gender ?? ""}
                defaultOption='Select Gender'
                nameExtractor={"description"}
                onChange={(e) => {
                  setPatient((prevState) => {
                    return { ...prevState, gender: e.target.value };
                  });
                  clearFieldErrors("gender");
                }}
                isInvalid={fieldErrors.gender}
              />
            </div>
            <div>
              <Select
                className={classes.dropDown}
                selectClass={classes.dropDownSelect}
                label={"Race"}
                htmlFor='race'
                options={activeRaces}
                value={patient?.race ?? ""}
                defaultOption='Select Race'
                nameExtractor={"description"}
                onChange={(e) => {
                  setPatient((prevState) => {
                    return { ...prevState, race: e.target.value };
                  });
                  clearFieldErrors("race");
                }}
                isInvalid={fieldErrors.race}
              />
            </div>
            <div>
              <Select
                className={classes.dropDown}
                selectClass={classes.dropDownSelect}
                label={"Ethnicity"}
                htmlFor='ethnicity'
                options={activeEthnicGroups}
                value={patient?.ethnicity ?? ""}
                defaultOption='Select Ethnicity'
                nameExtractor={"description"}
                onChange={(e) => {
                  setPatient((prevState) => {
                    return { ...prevState, ethnicity: e.target.value };
                  });
                  clearFieldErrors("ethnicity");
                }}
                isInvalid={fieldErrors.ethnicity}
              />
            </div>
            <div className={classes.inputAddress}>
              <Input
                inputClassName={classes.input}
                placeholder={"Enter Your Address"}
                value={patient.address}
                onChange={(e) => {
                  setPatient((prevState) => {
                    return { ...prevState, address: e.target.value };
                  });
                  clearFieldErrors("address");
                }}
                isInvalid={fieldErrors.address}
                name={"Address"}
              />
            </div>
            <div className={classes.inputZipcode}>
              <div>
                <Input
                  inputClassName={classes.input}
                  placeholder={"Enter Zipcode"}
                  type={"number"}
                  value={patient?.zipCode ?? ""}
                  onChange={(e) => {
                    setPatient((prevState) => {
                      return { ...prevState, zipCode: e.target.value };
                    });
                    clearFieldErrors("zipCode");
                  }}
                  isInvalid={fieldErrors.zipCode}
                  name={"Zip code"}
                />
              </div>
              <div>
                <Input
                  inputClassName={classes.input}
                  placeholder={"Enter Your City"}
                  value={patient.city}
                  onChange={(e) => {
                    setPatient((prevState) => {
                      return {
                        ...prevState,
                        city: e.target.value,
                      };
                    });
                    clearFieldErrors("city");
                  }}
                  isInvalid={fieldErrors.city}
                  name={"City"}
                />
              </div>
            </div>
            <div>
              <Select
                className={classes.dropDown}
                selectClass={classes.dropDownSelect}
                label={"Country"}
                htmlFor='country'
                options={[{ countryName: "USA", alphaCode: "US" }]}
                value={patient.country}
                nameExtractor={"countryName"}
                valueExtractor={"alphaCode"}
                onChange={(e) => {
                  setPatient((prevState) => {
                    return { ...prevState, country: e.target.value };
                  });
                  clearFieldErrors("country");
                }}
                isInvalid={fieldErrors.country}
              />
            </div>
            <div>
              <Select
                className={classes.dropDown}
                selectClass={classes.dropDownSelect}
                label={"State"}
                htmlFor='state'
                options={getStates}
                value={patient.state}
                nameExtractor={"stateName"}
                valueExtractor={"alphaCode"}
                onChange={(e) => {
                  setPatient((prevState) => {
                    return { ...prevState, state: e.target.value };
                  });
                  clearFieldErrors("state");
                }}
                isInvalid={fieldErrors.state}
              />
            </div>
            <InputLabel
              style={{ marginTop: "1rem" }}
              label='Do you have Medical Insurance?'
            />
            <div className={classes.actions}>
              {insuranceOptions.map((option, optionIdx) => (
                <SelectButton
                  key={optionIdx}
                  className={classes.action}
                  name={option}
                  onClick={() => {
                    setInsuranceOpt(option);
                    setPatient((prevState) => {
                      const newState = {
                        ...prevState,
                        haveInsurance:
                          option === InsuranceStatus.Yes ? true : false,
                      };
                      if (option === InsuranceStatus.No) {
                        newState.insuranceDetails = {
                          providerName: "",
                          policyNumber: "",
                          groupNumber: "",
                        };
                        newState.primaryMemberName = "";
                        newState.primaryMemberDob = "";
                        newState.secondaryInsuranceDetails = {
                          providerName: "",
                          policyNumber: "",
                          groupNumber: "",
                        };
                        newState.isPrimaryMember = false;
                        newState.haveSecondaryInsurance = false;
                        setPrimaryOpt(InsuranceStatus.No);
                        setSecondaryInsuranceOpt(InsuranceStatus.No);
                      }
                      return newState;
                    });
                  }}
                  active={insuranceOpt === option}
                />
              ))}
            </div>
            <div id='section3' className={classes.inputAddress}>
              <Input
                inputClassName={classes.input}
                placeholder={"Enter Insurance Company"}
                value={patient?.insuranceDetails?.providerName}
                onChange={(e) => {
                  setPatient((prevState) => {
                    return {
                      ...prevState,
                      insuranceDetails: {
                        ...prevState.insuranceDetails,
                        providerName: e.target.value,
                      },
                    };
                  });
                  clearFieldErrors("providerName");
                }}
                name={"Insurance Company"}
                disabled={!patient.haveInsurance}
                isInvalid={
                  patient.haveInsurance ? fieldErrors.providerName : false
                }
              />
            </div>
            <div className={classes.inputAddress}>
              <Input
                inputClassName={classes.input}
                placeholder={"Enter Policy Number"}
                value={patient?.insuranceDetails?.policyNumber}
                onChange={(e) => {
                  setPatient((prevState) => {
                    return {
                      ...prevState,
                      insuranceDetails: {
                        ...prevState.insuranceDetails,
                        policyNumber: e.target.value,
                      },
                    };
                  });
                  clearFieldErrors("policyNumber");
                }}
                name={"Policy Number"}
                disabled={!patient.haveInsurance}
                isInvalid={
                  patient.haveInsurance ? fieldErrors.policyNumber : false
                }
              />
            </div>
            <div className={classes.inputAddress}>
              <Input
                inputClassName={classes.input}
                placeholder={"Enter Group Number"}
                value={patient?.insuranceDetails?.groupNumber}
                onChange={(e) => {
                  setPatient((prevState) => {
                    return {
                      ...prevState,
                      insuranceDetails: {
                        ...prevState.insuranceDetails,
                        groupNumber: e.target.value,
                      },
                    };
                  });
                }}
                name={"Group Number"}
                disabled={!patient.haveInsurance}
              />
            </div>
            <>
              {patient.haveInsurance && (
                <>
                  <InputLabel
                    style={{ marginTop: "1rem" }}
                    label='Is the patient the primary member in the policy?'
                  />
                  <div className={classes.actions}>
                    {insuranceOptions.map((option, optionIdx) => (
                      <SelectButton
                        key={optionIdx}
                        className={classes.action}
                        name={option}
                        onClick={() => {
                          setPrimaryOpt(option);
                          setPatient((prevState) => {
                            const newState = {
                              ...prevState,
                              isPrimaryMember:
                                option === InsuranceStatus.Yes ? true : false,
                            };
                            if (option === InsuranceStatus.Yes) {
                              newState.primaryMemberName = "";
                              newState.primaryMemberDob = "";
                            }
                            return newState;
                          });
                        }}
                        active={primaryOpt === option}
                      />
                    ))}
                  </div>
                  <>
                    {primaryOpt === InsuranceStatus.No && (
                      <>
                        <div id='section3' className={classes.inputAddress}>
                          <Input
                            inputClassName={classes.input}
                            placeholder={"Enter Name"}
                            value={patient.primaryMemberName}
                            onChange={(e) => {
                              setPatient((prevState) => {
                                return {
                                  ...prevState,
                                  primaryMemberName: e.target.value,
                                };
                              });
                              clearFieldErrors("primaryMemberName");
                            }}
                            isInvalid={fieldErrors.primaryMemberName}
                            name={"Name of the primary member"}
                          />
                        </div>
                        <div id='section3' className={classes.inputAddress}>
                          <Input
                            type='date'
                            placeholder=''
                            min='1900-01-01'
                            max={getLocalDateString(new Date().getTime())}
                            value={
                              typeof patient.primaryMemberDob === "number"
                                ? getLocalDateString(patient.primaryMemberDob)
                                : ""
                            }
                            onChange={(e) => {
                              setPatient((prevState) => {
                                return {
                                  ...prevState,
                                  primaryMemberDob: getStartOfDayTimestamp(
                                    e.target.value
                                  ),
                                };
                              });
                              clearFieldErrors("primaryMemberDob");
                            }}
                            isInvalid={fieldErrors.primaryMemberDob}
                            invalidText={getDOBInvalidText(
                              patient.primaryMemberDob
                            )}
                            name={"Primary Member's Date of Birth"}
                          />
                        </div>
                      </>
                    )}
                  </>
                  <InputLabel
                    style={{ marginTop: "1rem" }}
                    label='Does the patient have a secondary policy?'
                  />
                  <div className={classes.actions}>
                    {insuranceOptions.map((option, optionIdx) => (
                      <SelectButton
                        key={optionIdx}
                        className={classes.action}
                        name={option}
                        onClick={() => {
                          setSecondaryInsuranceOpt(option);
                          setPatient((prevState) => {
                            const newState = {
                              ...prevState,
                              haveSecondaryInsurance:
                                option === InsuranceStatus.Yes ? true : false,
                            };
                            if (option === InsuranceStatus.No) {
                              newState.secondaryInsuranceDetails = {
                                groupNumber: "",
                                policyNumber: "",
                                providerName: "",
                              };
                            }
                            return newState;
                          });
                        }}
                        active={secondaryInsuranceOpt === option}
                      />
                    ))}
                  </div>
                  <>
                    {secondaryInsuranceOpt === InsuranceStatus.Yes && (
                      <>
                        <div id='section3' className={classes.inputAddress}>
                          <Input
                            inputClassName={classes.input}
                            placeholder={"Enter Insurance Company"}
                            value={
                              patient?.secondaryInsuranceDetails?.providerName
                            }
                            onChange={(e) => {
                              setPatient((prevState) => {
                                return {
                                  ...prevState,
                                  secondaryInsuranceDetails: {
                                    ...prevState.secondaryInsuranceDetails,
                                    providerName: e.target.value,
                                  },
                                };
                              });
                              clearFieldErrors("providerName2");
                            }}
                            name={"Insurance Company (Secondary)"}
                            isInvalid={
                              patient.haveSecondaryInsurance
                                ? fieldErrors.providerName2
                                : false
                            }
                          />
                        </div>
                        <div className={classes.inputAddress}>
                          <Input
                            inputClassName={classes.input}
                            placeholder={"Enter Policy Number"}
                            value={
                              patient?.secondaryInsuranceDetails?.policyNumber
                            }
                            onChange={(e) => {
                              setPatient((prevState) => {
                                return {
                                  ...prevState,
                                  secondaryInsuranceDetails: {
                                    ...prevState.secondaryInsuranceDetails,
                                    policyNumber: e.target.value,
                                  },
                                };
                              });
                              clearFieldErrors("policyNumber2");
                            }}
                            name={"Policy Number (Secondary)"}
                            isInvalid={
                              patient.haveSecondaryInsurance
                                ? fieldErrors.policyNumber2
                                : false
                            }
                          />
                        </div>
                        <div className={classes.inputAddress}>
                          <Input
                            inputClassName={classes.input}
                            placeholder={"Enter Group Number"}
                            value={
                              patient?.secondaryInsuranceDetails?.groupNumber
                            }
                            onChange={(e) => {
                              setPatient((prevState) => {
                                return {
                                  ...prevState,
                                  secondaryInsuranceDetails: {
                                    ...prevState.secondaryInsuranceDetails,
                                    groupNumber: e.target.value,
                                  },
                                };
                              });
                            }}
                            name={"Group Number (Secondary)"}
                          />
                        </div>
                      </>
                    )}
                  </>
                </>
              )}
            </>
            <div className={classes.infoQuestions}>
              {infoQuestions?.map((infoQuestion, infoQuestionIdx) => (
                <div key={infoQuestionIdx}>
                  <div className={classes.infoQuestionOptions}>
                    {infoQuestion.questionType === InfoQuestionType.FreeText &&
                      isQuestionVisible(infoQuestion) && (
                        <Input
                          inputClassName={classes.input}
                          placeholder={"Enter your response"}
                          value={
                            infoQuestionResponses[infoQuestionIdx]
                              ?.responseText || ""
                          }
                          onChange={(e) =>
                            infoQuestionResponseHandler(
                              infoQuestionIdx,
                              e.target.value
                            )
                          }
                          name={infoQuestion.question}
                          isInvalid={
                            fieldErrors.infoQuestions[infoQuestionIdx] || false
                          }
                        />
                      )}
                    {infoQuestion.questionType === InfoQuestionType.Number &&
                      isQuestionVisible(infoQuestion) && (
                        <Input
                          inputClassName={classes.input}
                          placeholder={"Enter your response"}
                          type='number'
                          value={
                            infoQuestionResponses[infoQuestionIdx]
                              ?.responseText || ""
                          }
                          onChange={(e) =>
                            infoQuestionResponseHandler(
                              infoQuestionIdx,
                              e.target.value
                            )
                          }
                          name={infoQuestion.question}
                          isInvalid={
                            fieldErrors.infoQuestions[infoQuestionIdx] || false
                          }
                        />
                      )}
                    {!infoQuestion.multiSelect &&
                      infoQuestion.questionType ===
                        InfoQuestionType.OptionList &&
                      isQuestionVisible(infoQuestion) && (
                        <Select
                          label={infoQuestion.question}
                          htmlFor='option'
                          options={infoQuestion.options.map((option) => {
                            return {
                              optionText: option,
                            };
                          })}
                          value={
                            infoQuestionResponses[infoQuestionIdx]
                              ?.responseText || ""
                          }
                          defaultOption='Select Option'
                          nameExtractor='optionText'
                          valueExtractor='optionText'
                          onChange={(e) =>
                            infoQuestionResponseHandler(
                              infoQuestionIdx,
                              e.target.value
                            )
                          }
                          isInvalid={
                            fieldErrors.infoQuestions[infoQuestionIdx] || false
                          }
                        />
                      )}
                    {infoQuestion.multiSelect &&
                      infoQuestion.questionType ===
                        InfoQuestionType.OptionList &&
                      isQuestionVisible(infoQuestion) && (
                        <MultiSelect
                          label={infoQuestion.question}
                          options={infoQuestion.options.map((option) => ({
                            label: option,
                            value: option,
                          }))}
                          value={
                            infoQuestionResponses[infoQuestionIdx]?.responseText
                              ? infoQuestionResponses[
                                  infoQuestionIdx
                                ]?.responseText
                                  .split("|")
                                  ?.map((res) => ({ label: res, value: res }))
                              : []
                          }
                          onChange={(selectedOptions) => {
                            const selectedValues = selectedOptions
                              .map(
                                (selectedOption: MultiSelectOption) =>
                                  selectedOption.value
                              )
                              .join("|");
                            infoQuestionResponseHandler(
                              infoQuestionIdx,
                              selectedValues
                            );
                          }}
                          isInvalid={
                            fieldErrors.infoQuestions[infoQuestionIdx] || false
                          }
                        />
                      )}
                    {infoQuestion.questionType === InfoQuestionType.Date &&
                      isQuestionVisible(infoQuestion) && (
                        <Input
                          type='date'
                          placeholder=''
                          value={
                            infoQuestionResponses[infoQuestionIdx]
                              ?.responseText || ""
                          }
                          onChange={(e) =>
                            infoQuestionResponseHandler(
                              infoQuestionIdx,
                              getLocalDateString(
                                getStartOfDayTimestamp(e.target.value)
                              )
                            )
                          }
                          name={infoQuestion.question}
                          isInvalid={
                            fieldErrors.infoQuestions[infoQuestionIdx] || false
                          }
                        />
                      )}
                  </div>
                </div>
              ))}
            </div>
            <div>
              {disclaimers.map((disclaimer, idx) => (
                <Checkbox
                  key={idx}
                  labelClassName={classes.disclaimerLabel}
                  className={classes.disclaimerCheckbox}
                  id={`disclaimer-${idx}`}
                  label={disclaimer.description}
                  checked={isDisclaimerChecked[idx] ?? false}
                  onChange={(checked) => {
                    setIsDisclaimerChecked((prevState) => {
                      const newState = [...prevState];
                      newState[idx] = checked;
                      return newState;
                    });
                  }}
                />
              ))}
            </div>
            <hr />
            <div>
              <Select
                className={classes.dropDown}
                selectClass={classes.dropDownSelect}
                label={"Relationship to Patient"}
                htmlFor='relationship'
                options={activeRelationships}
                value={orderData.relationshipToPatient}
                defaultOption='Select Your Relationship to Patient'
                nameExtractor={"description"}
                onChange={(e) => {
                  setOrderData((prevState) => {
                    return {
                      ...prevState,
                      relationshipToPatient: e.target.value,
                    };
                  });
                  clearFieldErrors("relationshipToPatient");
                }}
                isInvalid={fieldErrors.relationshipToPatient}
              />
            </div>
            <>
              {currEvent.regAllowSignatureOnFile && (
                <div className={classes.radioBtnContainer}>
                  <RadioInput
                    id='signature-on-file'
                    name='Signature on file'
                    checked={options.signatureOnFile === selected}
                    onChange={() => {
                      setSelected(options.signatureOnFile);
                      setSignatureDisclaimer(false);
                      clearSignatureHandler();
                    }}
                  />
                  <RadioInput
                    id='sign-here'
                    name='Sign here'
                    checked={options.signHere === selected}
                    onChange={() => setSelected(options.signHere)}
                  />
                </div>
              )}
            </>
            <div id='section4'>
              <Checkbox
                style={
                  currEvent.regAllowSignatureOnFile &&
                  options.signatureOnFile === selected
                    ? {
                        pointerEvents: "none",
                        opacity: 0.5,
                      }
                    : {}
                }
                labelClassName={classes.disclaimerLabel}
                className={classes.disclaimerCheckbox}
                id={`disclaimer-signature`}
                label={
                  "DISCLAIMER: By digitally signing your name below, you are signing this registration electronically. You agree that your electronic signature is the legal equivalent of your manual signature on this registration."
                }
                checked={signatureDisclaimer}
                onChange={(checked) => setSignatureDisclaimer(checked)}
              />
            </div>
            <div className={classes.canvasContainer}>
              <div
                className={getClasses(
                  classes.canvasActions,
                  !signatureDisclaimer ? classes.disabled : ""
                )}>
                <InputLabel label={"Signature:"} isInvalid={true} />
                <LinkButton label='CLEAR' onClick={clearSignatureHandler} />
              </div>
              <SignatureCanvas
                ref={padRef}
                penColor='black'
                canvasProps={{
                  className: getClasses(
                    classes.sigCanvas,
                    !signatureDisclaimer ? classes.disabled : ""
                  ),
                }}
                maxWidth={2}
              />
              {fieldErrors.signature && (
                <div className={classes.errorText}>This field is required</div>
              )}
            </div>
            <PrimaryButton
              className={classes.actionBtn}
              name='COMPLETE'
              onClick={addOrderHandler}
              disabled={
                !allDisclaimersAccepted ||
                ((!currEvent.regAllowSignatureOnFile ||
                  options.signHere === selected) &&
                  !signatureDisclaimer)
              }
            />
          </Card>
        </div>
        <div className={classes.order}>
          <Card>
            <OrderCard
              onClick={addOrderHandler}
              disabled={
                !allDisclaimersAccepted ||
                ((!currEvent.regAllowSignatureOnFile ||
                  options.signHere === selected) &&
                  !signatureDisclaimer)
              }
            />
          </Card>
        </div>
      </div>
    </>
  );
};

export default ContactDetails;
